// src/components/ui/CustomSelect.tsx
// System Design Custom Select Dropdown matching Dualon Edtech glassmorphism theme.
import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'

export type SelectOption<T extends string | number = string | number> = {
  value: T
  label: string
  icon?: React.ComponentType<{ size?: number; className?: string }>
}

interface CustomSelectProps<T extends string | number> {
  value: T
  options: SelectOption<T>[]
  onChange: (value: T) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  buttonClassName?: string
}

export default function CustomSelect<T extends string | number>({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
  buttonClassName = '',
}: CustomSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom')
  const ref = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value)

  useLayoutEffect(() => {
    if (!open) return
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      if (spaceBelow < 220 && spaceAbove > 180) {
        setPlacement('top')
      } else {
        setPlacement('bottom')
      }
    }
  }, [open])

  useEffect(() => {
    if (disabled) {
      setOpen(false)
      return
    }
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [disabled])

  const SelectedIcon = selected?.icon

  return (
    <div className={`relative w-full ${className}`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={`
          w-full flex items-center justify-between px-3 py-2 sm:py-2.5 rounded-xl
          border border-[var(--field-brd)] bg-[var(--field)] text-xs text-left
          font-semibold text-[var(--txt)] transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-[var(--mint-500)]/25 focus:border-[var(--mint-500)]
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[var(--mint-500)]/40 cursor-pointer'}
          ${buttonClassName}
        `}
      >
        <span className="flex items-center gap-2 truncate">
          {SelectedIcon && <SelectedIcon size={14} className="text-[var(--mint-500)] shrink-0" />}
          <span className={selected ? 'text-[var(--txt)] font-extrabold' : 'text-[var(--txt-3)] font-medium'}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`text-[var(--txt-3)] transition-transform duration-200 shrink-0 ml-1 ${
            open ? 'rotate-180 text-[var(--mint-500)]' : ''
          }`}
        />
      </button>

      {open && !disabled && (
        <div
          className={`
            absolute left-0 right-0 bg-[var(--bg-1)] border border-[var(--glass-brd)]
            rounded-xl shadow-2xl z-[200] max-h-[220px] overflow-y-auto scrollbar-none
            animate-in fade-in zoom-in-95 duration-150 backdrop-blur-md
            ${placement === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}
          `}
        >
          <div className="p-1 space-y-0.5">
            {options.map((opt) => {
              const active = opt.value === value
              const OptIcon = opt.icon
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange(opt.value)
                    setOpen(false)
                  }}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-xs
                    font-bold transition-all cursor-pointer
                    ${
                      active
                        ? 'bg-[var(--mint-500)]/10 text-[var(--mint-500)] font-extrabold'
                        : 'text-[var(--txt)] hover:bg-[var(--glass-brd)]/50'
                    }
                  `}
                >
                  <span className="flex items-center gap-2 truncate">
                    {OptIcon && <OptIcon size={14} className={active ? 'text-[var(--mint-500)]' : 'text-[var(--txt-3)]'} />}
                    <span>{opt.label}</span>
                  </span>
                  {active && <Check size={14} className="text-[var(--mint-500)] stroke-[2.5] shrink-0 ml-2" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
